import React, { useState } from 'react';
import { FileText, Bookmark, Trash2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { RedactionText } from '../../redaction/RedactionText';
import { useQuery } from 'react-query';
import { type PageBookmark } from '../../../services/bookmarkApi';
import { pageContextApi, type PageEvent } from '../../../services/pageContextApi';

interface BookmarksViewProps {
  bookmarks: PageBookmark[];
  documents: Array<{ documentId: string; filename: string }>;
  onOpenDocument: (request: { documentId: string; documentName: string; pageNumber?: number }) => void;
  onRemoveBookmark: (documentId: string, bookmarkId: string) => void;
}

function formatEventType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const SIGNIFICANCE_COLOURS: Record<string, string> = {
  MAJOR: 'bg-primary-500',
  SUPPORTING: 'bg-amber-500',
  ROUTINE: 'bg-medical-300 dark:bg-medical-600',
};

const EVENT_TYPE_COLOURS: Record<string, string> = {
  ADMISSION: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  DISCHARGE: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  DIAGNOSIS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  MAJOR_PROCEDURE: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  MEDICATION: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  LAB_RESULT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  INVESTIGATION: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const DEFAULT_TYPE_COLOUR = 'bg-medical-100 text-medical-600 dark:bg-medical-700 dark:text-medical-300';

export const BookmarksView: React.FC<BookmarksViewProps> = ({
  bookmarks,
  documents,
  onOpenDocument,
  onRemoveBookmark,
}) => {
  // Group bookmarks by documentId
  const grouped = bookmarks.reduce<Record<string, PageBookmark[]>>((acc, bm) => {
    if (!acc[bm.documentId]) acc[bm.documentId] = [];
    acc[bm.documentId].push(bm);
    return acc;
  }, {});

  // Sort bookmarks within each group by page number
  for (const docId of Object.keys(grouped)) {
    grouped[docId].sort((a, b) => a.pageNumber - b.pageNumber);
  }

  const docMap = new Map(documents.map((d) => [d.documentId, d.filename]));

  if (bookmarks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-medical-400">
        <Bookmark className="h-8 w-8 mb-2" />
        <p className="text-xs">No bookmarks yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([documentId, docBookmarks]) => {
        const filename = docMap.get(documentId) ?? 'Unknown document';

        return (
          <div key={documentId}>
            {/* Document group header */}
            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-medical-100 dark:border-medical-700">
              <FileText className="h-3.5 w-3.5 text-primary-500 shrink-0" />
              <span className="text-[11px] font-semibold text-medical-800 dark:text-medical-100 truncate flex-1 min-w-0">
                {filename}
              </span>
              <span className="text-[9px] text-medical-400 shrink-0 tabular-nums">
                {docBookmarks.length}
              </span>
            </div>

            {/* Bookmark items */}
            <div className="space-y-0.5 mt-0.5">
              {docBookmarks.map((bookmark) => (
                <BookmarkItem
                  key={bookmark.id}
                  bookmark={bookmark}
                  documentId={documentId}
                  documentName={filename}
                  onNavigate={() =>
                    onOpenDocument({
                      documentId,
                      documentName: filename,
                      pageNumber: bookmark.pageNumber,
                    })
                  }
                  onRemove={() => onRemoveBookmark(documentId, bookmark.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Bookmark Item ───────────────────────────────────────────────────────────

interface BookmarkItemProps {
  bookmark: PageBookmark;
  documentId: string;
  documentName: string;
  onNavigate: () => void;
  onRemove: () => void;
}

const BookmarkItem: React.FC<BookmarkItemProps> = ({ bookmark, documentId, onNavigate, onRemove }) => {
  const [eventsOpen, setEventsOpen] = useState(false);

  return (
    <div className="group rounded-md hover:bg-medical-50 dark:hover:bg-medical-700/30 transition-colors">
      {/* Main row */}
      <div className="flex items-start gap-2 px-2 py-1.5">
        {/* Colour dot */}
        <span
          className="mt-1.5 h-2.5 w-2.5 rounded-full shrink-0"
          style={{ backgroundColor: bookmark.color }}
        />

        {/* Content — clickable to navigate */}
        <button
          onClick={onNavigate}
          className="flex-1 min-w-0 text-left"
        >
          <p className="text-[11px] font-semibold text-medical-800 dark:text-medical-100">
            Page {bookmark.pageNumber}
          </p>
          {bookmark.note && (
            <p className="text-[11px] text-medical-600 dark:text-medical-300 line-clamp-2 leading-snug mt-0.5">
              {bookmark.note}
            </p>
          )}
          {!bookmark.note && (
            <p className="text-[10px] italic text-medical-300 dark:text-medical-600 mt-0.5">
              No note
            </p>
          )}
        </button>

        {/* Delete button (visible on hover) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded text-medical-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all shrink-0"
          title="Remove bookmark"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      {/* Events toggle */}
      <button
        onClick={() => setEventsOpen(prev => !prev)}
        className="flex items-center gap-1 px-2 pb-1.5 text-[9px] text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 transition-colors"
      >
        {eventsOpen ? (
          <ChevronDown className="h-2.5 w-2.5" />
        ) : (
          <ChevronRight className="h-2.5 w-2.5" />
        )}
        <span>Page events</span>
      </button>

      {/* Collapsible events */}
      {eventsOpen && (
        <BookmarkPageEvents documentId={documentId} pageNumber={bookmark.pageNumber} />
      )}
    </div>
  );
};

// ─── Lazy-loaded page events for a bookmark ──────────────────────────────────

const BookmarkPageEvents: React.FC<{ documentId: string; pageNumber: number }> = ({
  documentId,
  pageNumber,
}) => {
  const { data, isLoading } = useQuery(
    ['pageContext', documentId, pageNumber],
    () => pageContextApi.getPageContext(documentId, pageNumber),
    { staleTime: 5 * 60 * 1000, retry: 1 },
  );

  if (isLoading) {
    return (
      <div className="px-6 pb-2">
        <Loader2 className="h-3 w-3 text-medical-400 animate-spin" />
      </div>
    );
  }

  if (!data?.events || data.events.length === 0) {
    return (
      <div className="px-6 pb-2">
        <p className="text-[9px] text-medical-400 italic">No events on this page</p>
      </div>
    );
  }

  return (
    <div className="px-6 pb-2 space-y-1">
      {data.events.map((event: PageEvent) => (
        <div
          key={event.eventId}
          className="flex items-start gap-1.5"
        >
          {/* Significance dot */}
          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
            SIGNIFICANCE_COLOURS[event.significance] || SIGNIFICANCE_COLOURS.ROUTINE
          }`} />

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-medium text-medical-700 dark:text-medical-200 leading-tight line-clamp-2">
              <RedactionText text={event.headline} />
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {event.eventDate && (
                <span className="text-[8px] text-medical-400 tabular-nums">
                  {formatDate(event.eventDate)}
                </span>
              )}
              <span className={`px-1 py-0.5 text-[7px] font-medium rounded ${
                EVENT_TYPE_COLOURS[event.eventType] || DEFAULT_TYPE_COLOUR
              }`}>
                {formatEventType(event.eventType)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
